import { Box, Tabs, Tab, Typography, Card, CardContent, Grid } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState } from "react";
import { Synthesize } from "./Synthesize";
import { Synchronize } from "./Synchronize";
import { Routes, Route } from "react-router-dom";
import { useApplicationEntities } from "../../hooks/useApplicationEntities";
import { useDataModelHierarchy } from "../../hooks/useDataModelHierarchy";
import SchemaIcon from "@mui/icons-material/Schema";
import SyncIcon from "@mui/icons-material/Sync";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import StorageIcon from "@mui/icons-material/Storage";

export const DataManagement = () => {
  const [value, setValue] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { applications, totalApplications, totalEntities } = useApplicationEntities();
  const { domains } = useDataModelHierarchy();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `data-tab-${index}`,
      "aria-controls": `data-tabpanel-${index}`,
    };
  }

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`data-tabpanel-${index}`}
        aria-labelledby={`data-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  // Stats for the overview
  const coreSchemaCount = applications
    .filter(app => app.key === 'domain' || app.key === 'framework')
    .reduce((sum, app) => sum + app.entityCount, 0);
  
  // Count generated entities from Core Data Model domains
  const generatedEntityCount = domains.reduce((sum, domain) => sum + domain.schemas.length, 0);
  
  // Count contributing applications (excluding framework/platform)
  const contributingApps = applications.filter(app => 
    app.key !== 'framework' && app.key !== 'domain'
  ).length;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Data Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage core schemas, synchronize data, and oversee the solution-space data model
        </Typography>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SchemaIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Core Schemas</Typography>
              </Box>
              <Typography variant="h4">{coreSchemaCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Framework & Domain entities
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AutoFixHighIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Generated</Typography>
              </Box>
              <Typography variant="h4">{generatedEntityCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Generated application entities
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorageIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Applications</Typography>
              </Box>
              <Typography variant="h4">{contributingApps}</Typography>
              <Typography variant="body2" color="textSecondary">
                Contributing applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorageIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Entities</Typography>
              </Box>
              <Typography variant="h4">{totalEntities}</Typography>
              <Typography variant="body2" color="textSecondary">
                All discoverable entities
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="data management tabs"
        >
          <Tab 
            icon={<SyncIcon />}
            label="Synchronize" 
            {...a11yProps(0)} 
          />
          <Tab 
            icon={<AutoFixHighIcon />}
            label="Synthesize" 
            {...a11yProps(1)} 
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <CustomTabPanel value={value} index={0}>
        <Synchronize />
      </CustomTabPanel>
      
      <CustomTabPanel value={value} index={1}>
        <Synthesize />
      </CustomTabPanel>
    </Box>
  );
};

export default DataManagement;
